import { useState } from 'react'
import {
  X,
  Mail,
  Globe,
  MapPin,
  Briefcase,
  Building2,
  Clock,
  Check,
  Loader2,
  User,
} from 'lucide-react'
import type { ServiceRequest, ServiceRequestStatus } from '../../types/serviceRequest'
import { AUDIENCE_OPTIONS } from '../../types/serviceRequest'
import { ORGANIZATION_TYPE_OPTIONS, REQUEST_TYPE_OPTIONS } from '../../types/seeker'
import type { DeclineRequest } from '../../types/serviceRequest'

interface RequestDetailDrawerProps {
  request: ServiceRequest | null
  isOpen: boolean
  onClose: () => void
  onAccept: (id: string, data: DeclineRequest) => Promise<void>
  onDecline: (id: string, data: DeclineRequest) => Promise<void>
  isProcessing: boolean
}

export default function RequestDetailDrawer({
  request,
  isOpen,
  onClose,
  onAccept,
  onDecline,
  isProcessing,
}: RequestDetailDrawerProps) {
  const [responseMessage, setResponseMessage] = useState('')

  if (!request) return null

  const isNew = request.status === 'new'
  const seeker = request.seeker

  const getOrgTypeLabel = (value: string) =>
    ORGANIZATION_TYPE_OPTIONS.find((o) => o.value === value)?.label || value

  const getRequestTypeLabel = (value: string) =>
    REQUEST_TYPE_OPTIONS.find((o) => o.value === value)?.label || value

  const getAudienceLabel = (value: string) =>
    AUDIENCE_OPTIONS.find((o) => o.value === value)?.label || value

  const getStatusBadge = (status: ServiceRequestStatus) => {
    const styles: Record<string, string> = {
      new: 'bg-yellow-100 text-yellow-800',
      accepted: 'bg-blue-100 text-blue-800',
      awaiting_payment: 'bg-blue-100 text-blue-800',
      declined: 'bg-red-100 text-red-800',
      declined_requester: 'bg-red-100 text-red-800',
      in_progress: 'bg-green-100 text-green-800',
      completed: 'bg-green-100 text-green-800',
      refunded: 'bg-gray-100 text-gray-800',
    }
    const labels: Record<string, string> = {
      new: 'New',
      accepted: 'Accepted',
      awaiting_payment: 'Awaiting Payment',
      declined: 'Declined',
      declined_requester: 'Price Declined',
      in_progress: 'In Progress',
      completed: 'Completed',
      refunded: 'Refunded',
    }
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {labels[status] || status}
      </span>
    )
  }

  const getInitials = () => {
    const name = request.requester_full_name || `${seeker?.user_first_name || ''} ${seeker?.user_last_name || ''}`.trim() || seeker?.company_name || ''
    const parts = name.split(' ').filter(Boolean)
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
    return 'S'
  }

  const displayName = request.requester_full_name || `${seeker?.user_first_name || ''} ${seeker?.user_last_name || ''}`.trim() || seeker?.company_name || 'Unknown'

  const handleAccept = async () => {
    await onAccept(request.id, {
      message: responseMessage.trim() || undefined,
    })
    setResponseMessage('')
  }

  const handleDecline = async () => {
    await onDecline(request.id, {
      message: responseMessage.trim() || undefined,
    })
    setResponseMessage('')
  }

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/40 z-40 transition-opacity" onClick={onClose} />
      )}

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-full max-w-lg bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out overflow-y-auto ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-secondary-200 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-secondary-900">Request Details</h2>
            {getStatusBadge(request.status)}
          </div>
          <button
            onClick={onClose}
            className="p-2 text-secondary-400 hover:text-secondary-600 rounded-lg hover:bg-secondary-100 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Seeker Profile Card */}
          <div className="bg-secondary-50 rounded-xl p-5">
            <div className="flex items-start gap-4">
              {seeker?.profile_picture_url ? (
                <img
                  src={seeker.profile_picture_url}
                  alt={displayName}
                  className="h-14 w-14 rounded-full object-cover flex-shrink-0"
                />
              ) : (
                <div className="h-14 w-14 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                  {getInitials()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-secondary-900 text-lg">{displayName}</h3>
                {request.requester_email && (
                  <a href={`mailto:${request.requester_email}`} className="flex items-center gap-1.5 text-sm text-secondary-600 hover:text-primary-600 mt-0.5">
                    <Mail className="h-3.5 w-3.5" />
                    {request.requester_email}
                  </a>
                )}
                {(request.requester_organization_type || seeker?.organization_type) && (
                  <span className="flex items-center gap-1.5 text-sm text-secondary-500 mt-1">
                    <User className="h-3.5 w-3.5" />
                    {getOrgTypeLabel(request.requester_organization_type || seeker?.organization_type || '')}
                  </span>
                )}
              </div>
            </div>

            <div className="mt-4 space-y-2">
              {seeker?.company_name && (
                <div className="flex items-center gap-2 text-sm text-secondary-600">
                  <Building2 className="h-4 w-4 text-secondary-400 flex-shrink-0" />
                  {seeker.company_name}
                </div>
              )}
              {seeker?.industry && (
                <div className="flex items-center gap-2 text-sm text-secondary-600">
                  <Briefcase className="h-4 w-4 text-secondary-400 flex-shrink-0" />
                  {seeker.industry}
                </div>
              )}
              {(seeker?.country || seeker?.city) && (
                <div className="flex items-center gap-2 text-sm text-secondary-600">
                  <MapPin className="h-4 w-4 text-secondary-400 flex-shrink-0" />
                  {[seeker.city, seeker.country].filter(Boolean).join(', ')}
                </div>
              )}
              {seeker?.company_website && (
                <a
                  href={seeker.company_website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700"
                >
                  <Globe className="h-4 w-4 flex-shrink-0" />
                  {seeker.company_website.replace(/^https?:\/\//, '')}
                </a>
              )}
              {seeker?.description && (
                <p className="text-sm text-secondary-600 mt-2 pt-2 border-t border-secondary-200">
                  {seeker.description}
                </p>
              )}
            </div>
          </div>

          {/* Request Details */}
          <div>
            <h4 className="text-sm font-semibold text-secondary-900 uppercase tracking-wider mb-3">Request Details</h4>
            <div className="space-y-3">
              {request.request_type && (
                <div className="flex justify-between text-sm">
                  <span className="text-secondary-500">Type of request</span>
                  <span className="font-medium text-secondary-900">{getRequestTypeLabel(request.request_type)}</span>
                </div>
              )}
              {request.service_type && (
                <div className="flex justify-between text-sm">
                  <span className="text-secondary-500">Service</span>
                  <span className="font-medium text-secondary-900">{request.service_type.name}</span>
                </div>
              )}
              {request.topic && (
                <div className="flex justify-between text-sm">
                  <span className="text-secondary-500">Topic</span>
                  <span className="font-medium text-secondary-900">{request.topic}</span>
                </div>
              )}
              {request.intended_audience && request.intended_audience.length > 0 && (
                <div>
                  <span className="text-sm text-secondary-500 block mb-1.5">Intended audience</span>
                  <div className="flex flex-wrap gap-1.5">
                    {request.intended_audience.map((aud) => (
                      <span
                        key={aud}
                        className="inline-flex px-2.5 py-1 rounded-full text-xs font-medium bg-primary-50 text-primary-700 border border-primary-100"
                      >
                        {getAudienceLabel(aud)}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex items-center gap-1.5 text-sm text-secondary-400">
                <Clock className="h-3.5 w-3.5" />
                Requested {formatDate(request.created_at)}
              </div>
            </div>
          </div>

          {/* Original message */}
          <div>
            <h4 className="text-sm font-semibold text-secondary-900 uppercase tracking-wider mb-3">Message</h4>
            <div className="p-4 bg-secondary-50 rounded-lg">
              <p className="text-sm text-secondary-700 whitespace-pre-line">{request.message}</p>
            </div>
          </div>

          {/* Expert response (if already responded) */}
          {request.expert_message && (
            <div>
              <h4 className="text-sm font-semibold text-secondary-900 uppercase tracking-wider mb-3">Your Response</h4>
              <div className="p-4 bg-primary-50 rounded-lg border border-primary-100">
                <p className="text-sm text-primary-700">{request.expert_message}</p>
              </div>
            </div>
          )}

          {/* Actions (only for new requests) */}
          {isNew && (
            <div className="border-t border-secondary-200 pt-6 space-y-4">
              <h4 className="text-sm font-semibold text-secondary-900 uppercase tracking-wider">Respond</h4>

              {/* Response message */}
              <div>
                <label className="label">Message (optional)</label>
                <textarea
                  value={responseMessage}
                  onChange={(e) => setResponseMessage(e.target.value)}
                  placeholder="Add a message to the requester..."
                  className="input min-h-[80px] resize-none w-full"
                  rows={3}
                />
              </div>

              {/* Action buttons */}
              <div className="flex gap-3">
                <button
                  onClick={handleAccept}
                  disabled={isProcessing}
                  className="btn btn-accent flex-1 flex items-center justify-center gap-1.5"
                >
                  {isProcessing ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4" />
                  )}
                  Accept
                </button>
                <button
                  onClick={handleDecline}
                  disabled={isProcessing}
                  className="btn btn-secondary flex items-center gap-1.5"
                >
                  <X className="h-4 w-4" />
                  Decline
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
